import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card } from 'components/Card';
import When from 'components/When';
import { CLASS_ENTITY_TYPE } from 'constants/logic';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import ReasonSelection from './Steps/ReasonSelection';
import DisruptiveUserReportDetails from './Steps/DisruptiveUserReportDetails';
import ProcessingReport from './Steps/ProcessingReport';
import PaidSuccessReport from './Steps/PaidSuccessReport';
import UnpaidSuccessReport from './Steps/UnpaidSuccessReport';
import TrialAbuseReportDetails from './Steps/TrialAbuseReportDetails';

import './ReservationFeedbackModalV2.scss';

const { block } = bem('reservation-feedback-modal-v2');

const STEPS = {
  REASON_SELECTION: 'REASON_SELECTION',
  DISRUPTIVE_USER_REPORT_DETAILS: 'DISRUPTIVE_USER_REPORT_DETAILS',
  TRIAL_ABUSE_REPORT_DETAILS: 'TRIAL_ABUSE_REPORT_DETAILS',
  PROCESSING: 'PROCESSING',
  SUCCESS: 'SUCCESS',
};

const ReservationFeedbackModalV2 = ({
  disruptiveUserAlreadyReported,
  fullName,
  profileUrl,
  classEntityType,
  venueId,
  reservationId,
  resizeModal,
  scheduleId,
  trialAbuseAlreadyReported,
}) => {
  const isEligibleForTrialAbuseReport = classEntityType === CLASS_ENTITY_TYPE.CLASS;
  const [currentStep, setCurrentStep] = React.useState(STEPS.REASON_SELECTION);
  const [isPaid, setIsPaid] = React.useState(true);
  const [submitError, setSubmitError] = React.useState(false);

  useEffect(() => {
    if (currentStep === STEPS.SUCCESS && venueId != null && reservationId != null) {
      Segment.event('Viewed Report Submitted', { VID: venueId, RID: reservationId });
    }
  }, [currentStep, venueId, reservationId]);

  const setDisruptiveUserReportDetailsStep = () => {
    setSubmitError(false);
    setCurrentStep(STEPS.DISRUPTIVE_USER_REPORT_DETAILS);
  };

  const setTrialAbuseReportDetailsStep = () => {
    setSubmitError(false);
    setCurrentStep(STEPS.TRIAL_ABUSE_REPORT_DETAILS);
  };

  const handleDisruptiveUserSubmitError = () => {
    setSubmitError(true);
    setCurrentStep(STEPS.DISRUPTIVE_USER_REPORT_DETAILS);
  };

  const handleTrialAbuseSubmitError = () => {
    setSubmitError(true);
    setCurrentStep(STEPS.TRIAL_ABUSE_REPORT_DETAILS);
  };

  const setProcessingStep = () => {
    setCurrentStep(STEPS.PROCESSING);
  };

  const setSuccessStep = paid => {
    setIsPaid(paid);
    setCurrentStep(STEPS.SUCCESS);
  };

  return (
    <Card className={block()} data-cypress="reservation-feedback-modal-v2-confirm">
      <When predicate={currentStep === STEPS.REASON_SELECTION}>
        <ReasonSelection
          fullName={fullName}
          profileUrl={profileUrl}
          venueId={venueId}
          reservationId={reservationId}
          shouldShowTrialAbuseReason={isEligibleForTrialAbuseReport}
          setDisruptiveUserReportDetailsStep={setDisruptiveUserReportDetailsStep}
          setTrialAbuseReportDetailsStep={setTrialAbuseReportDetailsStep}
          disruptiveUserAlreadyReported={disruptiveUserAlreadyReported}
          trialAbuseAlreadyReported={trialAbuseAlreadyReported}
        />
      </When>
      <When predicate={currentStep === STEPS.DISRUPTIVE_USER_REPORT_DETAILS}>
        <DisruptiveUserReportDetails
          fullName={fullName}
          profileUrl={profileUrl}
          venueId={venueId}
          reservationId={reservationId}
          scheduleId={scheduleId}
          setProcessingStep={setProcessingStep}
          setSuccessStep={setSuccessStep}
          resizeModal={resizeModal}
          showSubmitError={submitError}
          onSubmitError={handleDisruptiveUserSubmitError}
        />
      </When>
      <When predicate={currentStep === STEPS.TRIAL_ABUSE_REPORT_DETAILS}>
        <TrialAbuseReportDetails
          fullName={fullName}
          profileUrl={profileUrl}
          venueId={venueId}
          reservationId={reservationId}
          scheduleId={scheduleId}
          setProcessingStep={setProcessingStep}
          setSuccessStep={setSuccessStep}
          resizeModal={resizeModal}
          showSubmitError={submitError}
          onSubmitError={handleTrialAbuseSubmitError}
        />
      </When>
      <When predicate={currentStep === STEPS.PROCESSING}>
        <ProcessingReport />
      </When>
      <When predicate={currentStep === STEPS.SUCCESS}>
        {isPaid ? (
          <PaidSuccessReport venueId={venueId} reservationId={reservationId} />
        ) : (
          <UnpaidSuccessReport venueId={venueId} reservationId={reservationId} />
        )}
      </When>
    </Card>
  );
};

ReservationFeedbackModalV2.propTypes = {
  fullName: PropTypes.string,
  profileUrl: PropTypes.string,
  classEntityType: PropTypes.string,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resizeModal: PropTypes.func,
  scheduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disruptiveUserAlreadyReported: PropTypes.bool,
  trialAbuseAlreadyReported: PropTypes.bool,
};

export default ReservationFeedbackModalV2;
